import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const IotReadings = () => {
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 20,
    total: 0,
  });

  useEffect(() => {
    fetchReadings(1);
  }, []);

  const fetchReadings = async (page = 1) => {
    setLoading(true);

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${API}/iot/readings?page=${page}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();

        setReadings(result.data);

        setPagination({
          currentPage: result.current_page,
          lastPage: result.last_page,
          perPage: result.per_page,
          total: result.total,
        });
      }
      else {
        console.error('Failed to list readings');
      }
    } catch (error) {
      console.error('Error fetching reading:', error);
    } finally {
      setLoading(false);
    }
  };

  const goToPage = (page) => {
    if (page < 1 || page > pagination.lastPage) 
      return;

    fetchReadings(page);
  };

  const formatTimestamp = (isoString) => {
    const date = new Date(isoString);
    const formatter = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'UTC',
      hour12: false
    });
    
    return formatter.format(date).replace(',', '');
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Iot Readings</h1>
          <p className="text-gray-600">List of raw data from IoT devices</p>
        </div>

      {/* Transaction Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : readings.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
            <p className="mt-2 text-gray-500">No data</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reading Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Metadata
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {readings.map((reading) => (
                  <tr key={reading.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 text-nowrap">{reading.device.device_name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 text-nowrap">{reading.value}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 text-nowrap">{reading.unit}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 text-nowrap">{reading.reading_type}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 text-nowrap">{formatTimestamp(reading.reading_timestamp)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 text-nowrap">{formatTimestamp(reading.created_at)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 text-nowrap">
                        {reading.metadata ? (
                          <pre className="text-xs bg-gray-50 p-1 rounded overflow-x-auto max-w-xs">
                            {JSON.stringify(reading.metadata, null, 2)}
                          </pre>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && pagination.lastPage > 1 && (
        <div className="flex justify-between items-center mt-6">
          <div className="texrt-sm text-gray-600">
            Showing {(pagination.currentPage - 1) * pagination.perPage + 1} 
            {' to ' } 
            {Math.min(pagination.currentPage * pagination.perPage, pagination.total)} 
            {' of '}
            {pagination.total} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => goToPage(1)}
              disabled={pagination.currentPage === 1}
              className={`px-3 py-1 rounded border ${pagination.currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              First
            </button>

            <button
              onClick={() => goToPage(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className={`px-3 py-1 rounded border ${pagination.currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              Previous
            </button>

            {[...Array(pagination.lastPage).keys()].slice(
              Math.max(0, pagination.currentPage - 3), 
              Math.min(pagination.lastPage, pagination.currentPage + 2)
            ).map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => goToPage(pageNum + 1)}
                className={`px-3 py-1 rounded border ${pagination.currentPage === pageNum + 1 ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                {pageNum + 1}
              </button>
            ))}

            <button
              onClick={() => goToPage(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.lastPage}
              className={`px-3 py-1 rounded border ${pagination.currentPage === pagination.lastPage ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              Next
            </button>

            <button
              onClick={() => goToPage(pagination.lastPage)}
              disabled={pagination.currentPage === pagination.lastPage}
              className={`px-3 py-1 rounded border ${pagination.currentPage === pagination.lastPage ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              Last
            </button>
          </div>
        </div>
      )}
      </div>
    </Layout>
  );
};

export default IotReadings;
